import { useCallback, useEffect, useState } from 'react'
import { KeyRound, Plus, Copy, Check, Ban, AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { apiService, ApiKey, PorteeCle } from '@/services/api'

/* Les portées, dites en français plutôt qu'en jargon. Le libellé doit suffire à
   décider : personne ne doit avoir à ouvrir le code pour savoir ce qu'il coche. */
const LIBELLE_PORTEE: Record<PorteeCle, string> = {
  'lecture': 'Lecture seule',
  'suivi-up-projets': 'Lecture + suivi UP-PROJETS',
  'synchro-upspot': 'Synchronisation UP-SPOT',
}

const AIDE_PORTEE: Record<PorteeCle, string> = {
  'lecture': "L'application pourra lire les entreprises et les contacts, et rien d'autre.",
  'suivi-up-projets':
    "En plus de la lecture, l'application pourra modifier les champs personnalisés d'une "
    + "entreprise — c'est ce qui permet à UP-PROJETS de décocher un suivi qu'on y a retiré. "
    + "Elle ne peut ni renommer, ni créer, ni supprimer quoi que ce soit.",
  'synchro-upspot':
    "Réservée à UP-SPOT : elle lui permet de prévenir UP-CRM dès qu'un compte est créé, "
    + "modifié ou supprimé, pour que la fiche contact suive aussitôt. Elle ne donne accès à "
    + "aucune donnée, pas même en lecture.",
}

/* Écran de gestion des clés d'API.

   Une clé donne à une application du parc UP le droit de LIRE les entreprises
   et les contacts, et rien d'autre. La première est destinée à UP-PROJETS, qui
   en a besoin pour rédiger les comptes rendus de réunion.

   L'écran est volontairement pauvre : créer, régénérer, révoquer. Il n'y a rien
   à modifier — la clé n'existe en clair qu'une seule fois, à son émission.

   Une clé oubliée ne se retrouve donc PAS. C'est le prix de ne garder qu'une
   empreinte, et c'est ce qui rend une fuite de la base inoffensive. La parade
   n'est pas de la retrouver mais de la remplacer : « Régénérer » émet une
   nouvelle clé sous le même nom et révoque l'ancienne dans le même geste. */

function dateCourte(valeur: string | null): string {
  if (!valeur) return '—'
  return new Date(valeur).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export default function ApiKeysCard() {
  const { toast } = useToast()
  const [cles, setCles] = useState<ApiKey[]>([])
  const [chargement, setChargement] = useState(true)
  const [creationOuverte, setCreationOuverte] = useState(false)
  const [etiquette, setEtiquette] = useState('')
  const [portee, setPortee] = useState<PorteeCle>('lecture')
  const [changementPortee, setChangementPortee] = useState<number | null>(null)
  const [creation, setCreation] = useState(false)
  const [revocation, setRevocation] = useState<number | null>(null)
  const [regeneration, setRegeneration] = useState<number | null>(null)

  // Clé fraîchement créée : affichée une seule fois, jamais rechargeable.
  const [cleEnClair, setCleEnClair] = useState<string | null>(null)
  const [copiee, setCopiee] = useState(false)

  const charger = useCallback(async () => {
    try {
      const reponse = await apiService.getApiKeys()
      if (reponse.success && reponse.data) setCles(reponse.data)
    } catch {
      toast({
        title: 'Lecture impossible',
        description: 'Les clés d\'API n\'ont pas pu être chargées.',
        variant: 'destructive',
      })
    } finally {
      setChargement(false)
    }
  }, [toast])

  useEffect(() => { void charger() }, [charger])

  const creer = async () => {
    const nom = etiquette.trim()
    if (!nom) return
    setCreation(true)
    try {
      const reponse = await apiService.createApiKey(nom, portee)
      if (reponse.success && reponse.data?.key) {
        setCleEnClair(reponse.data.key)
        setCopiee(false)
        setCreationOuverte(false)
        setEtiquette('')
        setPortee('lecture')
        await charger()
      } else {
        toast({ title: 'Création impossible', description: reponse.error, variant: 'destructive' })
      }
    } catch {
      toast({ title: 'Création impossible', variant: 'destructive' })
    } finally {
      setCreation(false)
    }
  }

  const revoquer = async (cle: ApiKey) => {
    setRevocation(cle.id)
    try {
      const reponse = await apiService.revokeApiKey(cle.id)
      if (reponse.success) {
        toast({
          title: 'Clé révoquée',
          description: `« ${cle.label} » est refusée dès maintenant.`,
        })
        await charger()
      }
    } catch {
      toast({ title: 'Révocation impossible', variant: 'destructive' })
    } finally {
      setRevocation(null)
    }
  }

  /* Une clé oubliée ne se retrouve pas : elle se remplace. L'ancienne est
     révoquée dans le même geste, pour ne pas laisser traîner un accès valide
     dont plus personne ne connaît la valeur. */
  const regenerer = async (cle: ApiKey) => {
    setRegeneration(cle.id)
    try {
      const reponse = await apiService.regenerateApiKey(cle.id)
      if (reponse.success && reponse.data?.key) {
        setCleEnClair(reponse.data.key)
        setCopiee(false)
        await charger()
      } else {
        toast({ title: 'Régénération impossible', description: reponse.error, variant: 'destructive' })
      }
    } catch {
      toast({ title: 'Régénération impossible', variant: 'destructive' })
    } finally {
      setRegeneration(null)
    }
  }

  /* Changer la portée d'une clé qui circule déjà : ouvrir ou refermer une
     serrure sans avoir à redistribuer le secret partout où il est posé. */
  const changerPortee = async (cle: ApiKey, suivante: PorteeCle) => {
    if (suivante === cle.portee) return
    setChangementPortee(cle.id)
    try {
      const reponse = await apiService.changerPorteeApiKey(cle.id, suivante)
      if (reponse.success) {
        toast({
          title: 'Portée modifiée',
          description: `« ${cle.label} » : ${LIBELLE_PORTEE[suivante]}.`,
        })
        await charger()
      } else {
        toast({ title: 'Modification impossible', description: reponse.error, variant: 'destructive' })
      }
    } catch {
      toast({ title: 'Modification impossible', variant: 'destructive' })
    } finally {
      setChangementPortee(null)
    }
  }

  const copier = async () => {
    if (!cleEnClair) return
    try {
      await navigator.clipboard.writeText(cleEnClair)
      setCopiee(true)
    } catch {
      // Le presse-papiers peut être refusé : la clé reste sélectionnable à la main.
      toast({
        title: 'Copie automatique refusée par le navigateur',
        description: 'Sélectionnez la clé et copiez-la vous-même.',
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" /> Clés d&apos;API
            </CardTitle>
            <CardDescription>
              Une clé permet à une autre application du parc UP de lire les entreprises et
              les contacts. Sa portée dit ce qu&apos;elle peut faire de plus — par défaut,
              rien : la lecture seule.
            </CardDescription>
          </div>
          <Button onClick={() => setCreationOuverte(true)}>
            <Plus className="h-4 w-4 mr-1" /> Nouvelle clé
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {chargement ? (
          <p className="text-sm text-gray-500">Chargement des clés…</p>
        ) : cles.length === 0 ? (
          <p className="text-sm text-gray-500">
            Aucune clé pour l&apos;instant. Créez-en une pour donner accès à une application.
          </p>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium">NOM</TableHead>
                  <TableHead className="font-medium">PORTÉE</TableHead>
                  <TableHead className="font-medium">DÉBUT DE LA CLÉ</TableHead>
                  <TableHead className="font-medium">CRÉÉE LE</TableHead>
                  <TableHead className="font-medium">DERNIÈRE UTILISATION</TableHead>
                  <TableHead className="font-medium">ÉTAT</TableHead>
                  <TableHead className="font-medium">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cles.map(cle => (
                  <TableRow key={cle.id} className={cle.revoked_at ? 'opacity-60' : undefined}>
                    <TableCell className="font-medium">{cle.label}</TableCell>
                    <TableCell>
                      {cle.revoked_at ? (
                        <span className="text-sm text-gray-400">{LIBELLE_PORTEE[cle.portee] ?? cle.portee}</span>
                      ) : (
                        <select
                          value={cle.portee}
                          disabled={changementPortee === cle.id}
                          onChange={e => void changerPortee(cle, e.target.value as PorteeCle)}
                          className="text-sm border rounded px-2 py-1 bg-white"
                          title="Ce que cette clé a le droit de faire"
                        >
                          {(Object.keys(LIBELLE_PORTEE) as PorteeCle[]).map(p => (
                            <option key={p} value={p}>{LIBELLE_PORTEE[p]}</option>
                          ))}
                        </select>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">upcrm_{cle.key_hint}…</TableCell>
                    <TableCell className="text-sm">{dateCourte(cle.created_at)}</TableCell>
                    <TableCell className="text-sm">
                      {cle.last_used_at
                        ? dateCourte(cle.last_used_at)
                        : <span className="text-gray-400">jamais utilisée</span>}
                    </TableCell>
                    <TableCell>
                      {cle.revoked_at
                        ? <Badge variant="secondary">Révoquée</Badge>
                        : <Badge>Active</Badge>}
                    </TableCell>
                    <TableCell>
                      {!cle.revoked_at && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={regeneration === cle.id}
                            title="Émettre une nouvelle clé sous le même nom et révoquer celle-ci"
                            onClick={() => regenerer(cle)}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            {regeneration === cle.id ? 'Régénération…' : 'Régénérer'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={revocation === cle.id}
                            onClick={() => revoquer(cle)}
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            {revocation === cle.id ? 'Révocation…' : 'Révoquer'}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Création */}
      <Dialog open={creationOuverte} onOpenChange={setCreationOuverte}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer une clé d&apos;API</DialogTitle>
            <DialogDescription>
              Donnez-lui le nom de l&apos;application qui l&apos;utilisera : c&apos;est ce nom
              qui vous permettra de savoir quoi révoquer, le jour venu.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="etiquette-cle">Nom de la clé</Label>
            <Input
              id="etiquette-cle"
              autoFocus
              value={etiquette}
              onChange={e => setEtiquette(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !creation) void creer() }}
              placeholder="UP-PROJETS — comptes rendus"
              maxLength={120}
            />
          </div>
          <div className="mt-3">
            <Label htmlFor="portee-cle">Portée</Label>
            <select
              id="portee-cle"
              value={portee}
              onChange={e => setPortee(e.target.value as PorteeCle)}
              className="w-full text-sm border rounded px-2 py-2 bg-white mt-1"
            >
              {(Object.keys(LIBELLE_PORTEE) as PorteeCle[]).map(p => (
                <option key={p} value={p}>{LIBELLE_PORTEE[p]}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">{AIDE_PORTEE[portee]}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreationOuverte(false)}>Annuler</Button>
            <Button onClick={creer} disabled={creation || !etiquette.trim()}>
              {creation ? 'Création…' : 'Créer la clé'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clé fraîchement émise — affichée une seule fois.

          La fenêtre ne se ferme ni au clic à côté ni avec Échap : c'est le seul
          moment où la clé existe en clair, et la refermer par mégarde oblige à
          en régénérer une. Seul le bouton explicite la ferme. */}
      <Dialog open={cleEnClair !== null} onOpenChange={ouvert => { if (!ouvert) setCleEnClair(null) }}>
        <DialogContent
          onPointerDownOutside={e => e.preventDefault()}
          onEscapeKeyDown={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Copiez cette clé maintenant
            </DialogTitle>
            <DialogDescription>
              Elle ne sera plus jamais affichée : nous n&apos;en gardons qu&apos;une empreinte,
              impossible à retourner. C&apos;est ce qui rend une fuite de la base sans
              conséquence. Si vous la perdez, le bouton « Régénérer » de la liste en
              émettra une nouvelle sous le même nom.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 break-all rounded-md bg-gray-100 px-3 py-2 text-xs font-mono select-all">
              {cleEnClair}
            </code>
            <Button variant="outline" size="sm" onClick={copier}>
              {copiee ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            L&apos;application réceptrice doit l&apos;envoyer dans l&apos;en-tête
            <code className="mx-1 rounded bg-gray-100 px-1">X-API-Key</code>.
            Elle ne doit jamais apparaître dans un navigateur.
          </p>
          <DialogFooter>
            <Button onClick={() => setCleEnClair(null)}>
              {copiee ? 'J\'ai copié la clé, fermer' : 'Fermer sans copier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
